import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { LucideIcon } from "lucide-react"
import { ToastTitle, ToastDescription, ToastActionElement } from "@/components/ui/toast"

export type ToastVariant = "success" | "error" | "info"

interface EnhancedToastOptions {
  variant?: ToastVariant
  title?: string
  description?: string
  icon?: LucideIcon
  action?: ToastActionElement
  className?: string
  duration?: number
  iconClassName?: string
  iconContainerClassName?: string
}

const variantStyles: Record<ToastVariant, { 
  container: string
  iconContainer: string
  icon: string
  title: string
  description: string
}> = {
  success: {
    container: "bg-background dark:bg-card border-green-200/80 dark:border-green-800/80",
    iconContainer: "bg-green-500/10 dark:bg-green-400/10 ring-1 ring-green-500/20 dark:ring-green-400/20",
    icon: "text-green-600 dark:text-green-400",
    title: "text-foreground",
    description: "text-muted-foreground",
  },
  error: {
    container: "bg-background dark:bg-card border-red-200/80 dark:border-red-800/80",
    iconContainer: "bg-red-500/10 dark:bg-red-400/10 ring-1 ring-red-500/20 dark:ring-red-400/20",
    icon: "text-red-600 dark:text-red-400",
    title: "text-foreground",
    description: "text-muted-foreground",
  },
  info: {
    container: "bg-background dark:bg-card border-blue-200/80 dark:border-blue-800/80",
    iconContainer: "bg-blue-500/10 dark:bg-blue-400/10 ring-1 ring-blue-500/20 dark:ring-blue-400/20",
    icon: "text-blue-600 dark:text-blue-400",
    title: "text-foreground",
    description: "text-muted-foreground",
  },
}

export function useEnhancedToast() {
  const { toast } = useToast()

  const showToast = ({
    variant = "info",
    title,
    description,
    icon: Icon,
    action,
    className,
    duration = 4000,
    iconClassName,
    iconContainerClassName,
  }: EnhancedToastOptions) => {
    const styles = variantStyles[variant]

    const titleContent = title && (
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <div className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            styles.iconContainer,
            iconContainerClassName
          )}>
            <Icon className={cn("h-4 w-4 stroke-[2]", styles.icon, iconClassName)} />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-1">
          <ToastTitle className={cn("text-sm font-medium leading-none", styles.title)}>
            {title}
          </ToastTitle>
          {description && (
            <ToastDescription className={cn("text-xs leading-relaxed", styles.description)}>
              {description}
            </ToastDescription>
          )}
        </div>
      </div>
    )

    toast({
      duration,
      className: cn(
        "group pointer-events-auto relative border shadow-lg backdrop-blur-sm",
        styles.container,
        className
      ),
      title: titleContent as string,
      description: undefined, // We handle description inline with title
      action,
    })
  }

  return {
    toast: showToast,
    success: (options: Omit<EnhancedToastOptions, "variant">) =>
      showToast({ ...options, variant: "success" }),
    error: (options: Omit<EnhancedToastOptions, "variant">) =>
      showToast({ ...options, variant: "error" }),
    info: (options: Omit<EnhancedToastOptions, "variant">) =>
      showToast({ ...options, variant: "info" }),
  }
}
